import { Modal, Input, Form, message, Alert } from 'antd';

interface Props {
  oid: string;
  displayName: string;
  open: boolean;
  onClose: () => void;
  onPurged: () => void;
  /**
   * Injected API call — each SPA passes its own `/v1/admin/*` or
   * `/v1/superadmin/*` purge implementation. The typed `DELETE` confirmation
   * and reason are forwarded verbatim (the backend re-validates both).
   */
  onPurge: (
    oid: string,
    body: { confirmation: string; reason: string },
  ) => Promise<void>;
}

export function PurgeUserModal({
  oid,
  displayName,
  open,
  onClose,
  onPurged,
  onPurge,
}: Props) {
  const [form] = Form.useForm<{ confirmation: string; reason: string }>();
  const confirmation = Form.useWatch('confirmation', form);
  const reason = Form.useWatch('reason', form);
  const canSubmit = confirmation === 'DELETE' && (reason?.length ?? 0) >= 10;

  return (
    <Modal
      open={open}
      title={`Permanently delete ${displayName}?`}
      okText="Permanently delete"
      okButtonProps={{ danger: true, disabled: !canSubmit }}
      onCancel={onClose}
      onOk={async () => {
        try {
          await onPurge(oid, { confirmation: confirmation!, reason: reason! });
          void message.success(`${displayName} has been permanently deleted.`);
          onPurged();
          onClose();
        } catch (err: unknown) {
          const msg =
            err instanceof Error
              ? err.message
              : (err as { message?: string })?.message ?? 'Purge failed';
          void message.error(msg);
        }
      }}
    >
      <Alert
        type="error"
        showIcon
        message="This action cannot be undone."
        description="The user's Entra account, profile, and assignments will be permanently removed. Their captures stay in the system but are anonymized — historical data continuity for managers."
      />
      <br />
      <Form form={form} layout="vertical">
        <Form.Item
          name="confirmation"
          label={`Type "DELETE" to confirm`}
          rules={[{ required: true }]}
        >
          <Input placeholder="DELETE" />
        </Form.Item>
        <Form.Item
          name="reason"
          label="Reason (required, ≥10 chars)"
          rules={[{ required: true, min: 10 }]}
        >
          <Input.TextArea
            rows={3}
            placeholder="e.g. GDPR right-to-erasure request from user 2026-06-01"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
